import { useState } from "react";
import CopyCode from "../../CopyCode/CopyCode";

const ScrollIndicator = () => {
  return (
    <div className="flex flex-col gap-y-10 p-6">
      {/* BASIC SCROLL INDICATORS */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-white mb-6">Scroll Indicators</h2>
        <div className="grid grid-cols-1 gap-4">
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Progress Bar Top</label>
            <div className="relative">
              <div className="w-full h-1 bg-slate-700 rounded">
                <div className="h-full bg-amber-400" style={{width: "45%"}}></div>
              </div>
              <div className="mt-2 h-32 bg-slate-950 rounded border border-slate-600 overflow-y-auto text-slate-400 text-sm p-2">
                <p>Scrollable content here...</p>
                {[1,2,3,4,5].map(i => <p key={i}>Line {i}</p>)}
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="w-full h-1 bg-slate-700 rounded">
  <div className="h-full bg-amber-500" style={{width: "45%"}}></div>
</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Scroll Position Dots</label>
            <div className="p-2 h-32 bg-slate-950 rounded border border-slate-600 overflow-y-auto text-slate-400 text-sm">
              {[1,2,3,4,5,6,7,8].map(i => <p key={i} className="pb-2">Section {i}</p>)}
            </div>
            <div className="flex justify-center gap-1 mt-2">
              {[1, 2, 3, 4, 5].map((i, idx) => (
                <div key={i} className={`w-2 h-2 rounded-full ${idx === 1 ? "bg-amber-400" : "bg-slate-600"}`}></div>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="flex justify-center gap-1">
  {sections.map((s, i) => (
    <div className={i === active ? "bg-amber-400" : "bg-slate-600"} />
  ))}
</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Skeleton Dots</label>
            <div className="p-2 h-32 bg-slate-950 rounded border border-slate-600 overflow-y-auto text-slate-400 text-sm">
              {[1,2,3,4].map(i => <p key={i} className="pb-3">Content block {i}</p>)}
            </div>
            <div className="flex justify-center gap-2 mt-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className={`w-3 h-3 rounded-full border-2 border-slate-600 ${i <= 2 ? "bg-amber-400 border-amber-400" : ""}`}></div>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="flex justify-center gap-2">
  {items.map(i => (
    <div className="w-3 h-3 rounded-full border-2" />
  ))}
</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Step Indicator</label>
            <div className="flex justify-between text-xs mb-2">
              <span className="text-amber-400">Step 1</span>
              <span className="text-slate-500">Step 2</span>
              <span className="text-slate-500">Step 3</span>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 h-2 bg-amber-400 rounded"></div>
              <div className="flex-1 h-2 bg-slate-700 rounded"></div>
              <div className="flex-1 h-2 bg-slate-700 rounded"></div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="flex gap-2">
  <div className="flex-1 h-2 bg-amber-500 rounded"></div>
  <div className="flex-1 h-2 bg-slate-700 rounded"></div>
  <div className="flex-1 h-2 bg-slate-700 rounded"></div>
</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Loading Bar</label>
            <div className="w-full h-1 bg-slate-700 rounded overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-500 to-amber-500 animate-pulse" style={{width: "60%"}}></div>
            </div>
            <p className="text-xs text-slate-500 mt-2">60% complete</p>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="w-full h-1 bg-slate-700 rounded overflow-hidden">
  <div className="h-full bg-gradient-to-r from-amber-500 to-amber-500 animate-pulse" />
</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Circular Progress</label>
            <div className="flex items-center justify-center gap-4">
              <div className="relative w-16 h-16">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#334155" strokeWidth="4"/>
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#06B6D4" strokeWidth="4" strokeDasharray="88" strokeDashoffset="22"/>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-sm font-bold">75%</div>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<svg className="w-16 h-16 transform -rotate-90">
  <circle cx="32" cy="32" r="28" fill="none" stroke="#334155" strokeWidth="4"/>
  <circle cx="32" cy="32" r="28" fill="none" stroke="#06B6D4" strokeDasharray="88" />
</svg>`} />
          </div>
        </div>
      </div>

      {/* ADVANCED INDICATORS */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-amber-400 mb-6">Advanced Variants</h2>
        <div className="grid grid-cols-1 gap-4">
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Multi-color Progress</label>
            <div className="space-y-2">
              <div className="text-xs text-slate-400">Completed</div>
              <div className="flex gap-1">
                <div className="flex-1 h-2 bg-green-500 rounded"></div>
                <div className="flex-1 h-2 bg-green-500 rounded"></div>
                <div className="flex-1 h-2 bg-yellow-500 rounded"></div>
                <div className="flex-1 h-2 bg-slate-700 rounded"></div>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="flex gap-1">
  <div className="flex-1 h-2 bg-green-500"></div>
  <div className="flex-1 h-2 bg-yellow-500"></div>
  <div className="flex-1 h-2 bg-slate-700"></div>
</div>`} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScrollIndicator;


